import { Image } from 'expo-image';
import { type Href, Link } from 'expo-router';
import { useMemo } from 'react';
import { PixelRatio, Image as ReactNativeImage, StyleSheet, View } from 'react-native';

import { Pressable, Text, tokens } from '@fhs/ui';

import type { MenuSection } from '../types';

const MENU_ITEM_HEIGHT = 90;

export function MenuListItemRow<T extends MenuSection['items'][number]>({
  item,
  index,
  onPress,
  href,
}: {
  item: T;
  index: number;
  onPress: (item: T) => void;
  href?: Href;
}) {
  const imageUri = useMemo(
    () => getSizedImageUri(item.image?.asset?.uri),
    [item.image?.asset?.uri]
  );
  const badge = item.badge?.toUpperCase();
  const isAvailable = item.isAvailable;

  const row = (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={[!isAvailable && 'Out of stock', item.displayName, item.descriptionShort]
        .filter(Boolean)
        .join(', ')}
      disabled={!isAvailable}
      onPress={() => onPress(item)}
      borderRadius={0}
      style={StyleSheet.flatten([styles.item, !isAvailable && styles.unavailable])}
    >
      <View style={styles.imageContainer}>
        <Image
          recyclingKey={`${item.slug}-${index}`}
          source={imageUri ? { uri: imageUri } : null}
          placeholder={
            item.image?.asset?.blurHash ? { blurhash: item.image.asset.blurHash } : undefined
          }
          accessibilityLabel={item.image?.altText || item.displayName}
          contentFit="contain"
          style={styles.itemImage}
        />
        {badge === 'NEW' ? (
          <View style={styles.newBadge} accessibilityLabel="New item">
            <ReactNativeImage
              source={require('../assets/img/menu-item-new-badge-native.png')}
              resizeMode="contain"
              style={styles.newBadgeBackground}
            />
            <Text weight="bold" style={styles.newBadgeText}>
              NEW
            </Text>
          </View>
        ) : badge === 'LIMITED' ? (
          <View style={styles.limitedBadge}>
            <Text.Ui size="xs" weight="semibold" style={styles.badgeText}>
              LIMITED
            </Text.Ui>
          </View>
        ) : null}
      </View>

      <View style={styles.itemText}>
        <Text.Heading type="four" numberOfLines={2} style={styles.itemTitle}>
          {item.displayName}
        </Text.Heading>
        {item.descriptionShort ? (
          <Text.Paragraph size="sm" numberOfLines={2} style={styles.itemDescription}>
            {item.descriptionShort}
          </Text.Paragraph>
        ) : null}
      </View>
    </Pressable>
  );

  return href ? (
    <Link href={href} asChild>
      {row}
    </Link>
  ) : row;
}

function getSizedImageUri(baseUri?: string | null) {
  if (!baseUri) {
    return undefined;
  }

  try {
    const imageUrl = new URL(baseUri);
    imageUrl.searchParams.set('h', '250');
    // Menu API URLs include w=275; scale those dimensions for high-density screens.
    imageUrl.searchParams.set('dpr', Math.min(Math.max(PixelRatio.get(), 1), 5).toString());
    imageUrl.searchParams.set('fit', 'max');
    imageUrl.searchParams.set('fm', 'webp');
    return imageUrl.toString();
  } catch {
    return baseUri;
  }
}

const styles = StyleSheet.create({
  item: {
    minHeight: MENU_ITEM_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: tokens.colors.$white,
  },
  unavailable: {
    opacity: 0.6,
  },
  imageContainer: {
    width: 132,
    height: MENU_ITEM_HEIGHT,
    overflow: 'hidden',
    borderRadius: 8,
  },
  itemImage: {
    width: 132,
    height: MENU_ITEM_HEIGHT,
  },
  newBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 41,
    height: 31,
    alignItems: 'center',
    justifyContent: 'center',
  },
  newBadgeBackground: {
    ...StyleSheet.absoluteFillObject,
  },
  newBadgeText: {
    color: tokens.colors.$white,
    fontFamily: 'HeroSerifBold',
    fontSize: 12,
    lineHeight: 17,
  },
  limitedBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    paddingHorizontal: 6,
    paddingVertical: 3,
    backgroundColor: tokens.colors.$houseRed,
  },
  badgeText: {
    color: tokens.colors.$white,
  },
  itemText: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  itemTitle: {
    lineHeight: 20,
  },
  itemDescription: {
    lineHeight: 17,
  },
});
